import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from '@/lib/utils';

const buttonVariants = cva(
  'border-transparent border inline-flex items-center justify-center whitespace-nowrap rounded-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none',
  {
    variants: {
      variant: {
        default: 'bg-primary text-primary-foreground hover:bg-primary/80',
        destructive:
          'bg-destructive text-destructive-foreground hover:bg-destructive/90',
        destructiveOutline:
          'bg-none border border-destructive text-destructive hover:bg-destructive/90',
        outline:
          'border border-input bg-background hover:bg-accent hover:text-accent-foreground',
        secondary:
          'bg-secondary text-secondary-foreground hover:bg-secondary/80',
        ghost: 'hover:bg-accent hover:text-accent-foreground',
        link: 'text-primary underline-offset-4 hover:underline',
        linkSm: 'text-primary text-sm font-normal',
        icon: 'bg-none hover:bg-white hover:text-accent-foreground hover:border hover:border-gray-400',
        disabled:
          'bg-background-tertiary text-text-disabled hover:bg-background-tertiary hover:text-text-disabled border-border-button',
      },
      size: {
        default: 'px-4 py-2',
        xs: 'text-xs px-2 py-1',
        sm: 'px-3 py-[6px]',
        lg: 'px-6 py-4 text-[16px]',
        iconSm: 'h-fit max-h-9 w-fit py-1 px-1',
        icon: 'h-fit min-h-9 max-h-9 w-fit py-2 px-2 min-w-9',
        link: 'p-0',
        iconLg: 'h-12 w-12 flex items-center justify-center',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  }
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  icon?: React.ReactNode;
  overrideDisabled?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant,
      size,
      asChild = false,
      icon,
      children,
      disabled,
      type = 'button',
      overrideDisabled = false,
      ...props
    },
    ref
  ) => {
    const Comp = asChild ? Slot : 'button';
    return (
      <Comp
        className={cn(
          buttonVariants({
            variant: disabled && overrideDisabled ? 'disabled' : variant,
            size,
            className,
          }),
          'flex items-center gap-1'
        )}
        ref={ref}
        disabled={disabled}
        type={type}
        {...props}
      >
        {icon && icon}
        {children && children}
      </Comp>
    );
  }
);
Button.displayName = 'Button';

export { Button, buttonVariants };
